import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { CustomSituViewWebApiClient } from '../../../../services/custom-situ-view-web-api-client.service';
import { ConfigService } from '../../../../services/config.service';
import {
  SearchProductUsageRequest,
  SearchProductUsageResponse,
  ProductUsageEntry,
  ProductUsageSighting,
  GetProductUsageSightingsRequest,
  IProduct2
} from 'situview-webapi-client';
import {
  ListProductImpressionsRequest,
  SituAnalyticsWebApiClient
} from 'situanalytics-webapi-client';

@Component({
  selector: 'product-discover-tab',
  templateUrl: './product-discover-tab.component.html',
  styleUrls: ['./product-discover-tab.component.scss']
})
export class ProductDiscoverTabComponent implements OnInit {
  @Input() product!: IProduct2;
  @Input() searchSku?: string;
  @Input() searchManufacturer?: string;
  @Input() searchProductId?: string;
  @Input() autoSearch: boolean = true;
  private _paginator!: MatPaginator;
  private _sort!: MatSort;

  @ViewChild(MatPaginator) set matPaginator(p: MatPaginator) {
    this._paginator = p;
    if (this.dataSource && p) this.dataSource.paginator = p;
  }
  get paginator(): MatPaginator { return this._paginator; }

  @ViewChild(MatSort) set matSort(s: MatSort) {
    this._sort = s;
    if (this.dataSource && s) this.dataSource.sort = s;
  }
  get sort(): MatSort { return this._sort; }

  isLoading = false;
  displayedColumns: string[] = ['homeName', 'companyName', 'sightingCount', 'replacementCount', 'hotspotCount', 'impressionCount', 'rooms', 'actions'];

  readonly impressionWindowHours = 24;
  loadingImpressions = false;

  dataSource!: MatTableDataSource<ProductUsageEntry>;
  totalResults = 0;
  allRecords: ProductUsageEntry[] = [];
  situViewUrl: string = '';

  expandedRecord: ProductUsageEntry | null = null;
  expandedRecordSightings: Map<string, ProductUsageSighting[]> = new Map();
  loadingSightings: Set<string> = new Set();

  constructor(
    private apiClient: CustomSituViewWebApiClient,
    private snackBar: MatSnackBar,
    private router: Router,
    private configService: ConfigService,
    private situAnalyticsClient: SituAnalyticsWebApiClient
  ) {}

  ngOnInit(): void {
    this.dataSource = new MatTableDataSource<ProductUsageEntry>([]);
    this.situViewUrl = this.configService.config?.situViewWebApiBaseUrl || '';
    if (this.autoSearch) this.loadProductUsage();
  }

  getProductManufacturer(): string | undefined {
    const product = this.product as any;
    return product?.brand?.name || product?.manufacturer;
  }

  async loadProductUsage(): Promise<void> {
    this.isLoading = true;
    try {
      const productId = this.searchProductId || this.product?.id;
      const sku = this.searchSku || this.product?.sku;
      const manufacturer = this.searchManufacturer || this.getProductManufacturer();

      const request = productId
        ? new SearchProductUsageRequest({ productId, limit: 1000 })
        : new SearchProductUsageRequest({ sku: sku || undefined, manufacturer: manufacturer || undefined, limit: 1000 });

      const response = await this.apiClient.searchProductUsage(request);
      if (response.success) {
        this.handleResults(response);
      } else {
        this.snackBar.open(response.errorMessages?.join(', ') || 'Failed to load', 'Dismiss', { duration: 3000 });
        this.clearResults();
      }
    } catch (error) {
      console.error('Error loading product usage:', error);
      this.snackBar.open('An error occurred', 'Dismiss', { duration: 3000 });
      this.clearResults();
    } finally {
      this.isLoading = false;
    }
  }

  private handleResults(response: SearchProductUsageResponse): void {
    const records = response.records || [];
    records.forEach(r => (r as any).impressionCount = 0);
    this.allRecords = records;
    this.dataSource.data = records;
    this.totalResults = records.length;
    this.expandedRecord = null;
    this.expandedRecordSightings.clear();
    if (this.paginator) this.paginator.firstPage();
    this.loadImpressionCounts(records);
  }

  private async loadImpressionCounts(records: ProductUsageEntry[]): Promise<void> {
    if (!records.length) return;
    this.loadingImpressions = true;
    try {
      const dateTo = new Date();
      const dateFrom = new Date(dateTo.getTime() - this.impressionWindowHours * 60 * 60 * 1000);

      const sku = this.searchSku || this.product?.sku || undefined;
      const manufacturer = this.searchManufacturer || this.getProductManufacturer() || undefined;

      const request = new ListProductImpressionsRequest({
        dateFrom,
        dateTo,
        productSku: sku,
        manufacturer,
        pageNumber: 1,
        pageSize: 1000
      });

      const response = await this.situAnalyticsClient.listProductImpressions(request);
      const items = response?.items || [];
      const counts = new Map<string, number>();
      for (const item of items) {
        if (!item.artifactId || !item.productId) continue;
        const key = `${item.artifactId}|${item.productId}`;
        counts.set(key, (counts.get(key) || 0) + (item.totalImpressions || 0));
      }

      for (const record of records) {
        if (!record.renderedOutputId || !record.productId) continue;
        const key = `${record.renderedOutputId}|${record.productId}`;
        (record as any).impressionCount = counts.get(key) || 0;
      }
      this.dataSource.data = [...records];
    } catch (error) {
      console.error('Error loading impression counts:', error);
    } finally {
      this.loadingImpressions = false;
    }
  }

  clearResults(): void {
    this.dataSource.data = [];
    this.allRecords = [];
    this.totalResults = 0;
    this.expandedRecord = null;
    this.expandedRecordSightings.clear();
  }

  toggleExpand(record: ProductUsageEntry): void {
    if (this.expandedRecord === record) {
      this.expandedRecord = null;
      return;
    }
    this.expandedRecord = record;
    const id = record.recordId || '';
    if (!id || this.expandedRecordSightings.has(id)) return;
    this.loadSightings(record);
  }

  private async loadSightings(record: ProductUsageEntry): Promise<void> {
    const id = record.recordId || '';
    if (!id) return;
    this.loadingSightings.add(id);
    try {
      const response = await this.apiClient.getProductUsageSightings(
        new GetProductUsageSightingsRequest({ recordId: id })
      );
      if (response.success) {
        this.expandedRecordSightings.set(id, response.sightings || []);
      }
    } catch (error) {
      console.error('Error loading sightings:', error);
    } finally {
      this.loadingSightings.delete(id);
    }
  }

  getSightings(record: ProductUsageEntry): ProductUsageSighting[] {
    return this.expandedRecordSightings.get(record.recordId || '') || [];
  }

  isSightingsLoading(record: ProductUsageEntry): boolean {
    return this.loadingSightings.has(record.recordId || '');
  }

  viewSighting(record: ProductUsageEntry, sighting: ProductUsageSighting): void {
    const baseUrl = this.configService.config?.situLinkBaseUrl
      ? `https://view.${this.configService.config.situLinkBaseUrl}`
      : (this.configService.config?.situPortalBaseUrl || window.location.origin);
    const url = new URL(baseUrl);
    url.pathname = record.renderedOutputId || '/';
    if (sighting.panoramaName) url.searchParams.set('panorama', sighting.panoramaName);
    if (sighting.yaw !== undefined) url.searchParams.set('hlookat', String(Math.round(sighting.yaw)));
    if (sighting.pitch !== undefined) url.searchParams.set('vlookat', String(Math.round(-sighting.pitch)));
    window.open(url.toString(), '_blank');
  }

  viewHome(record: ProductUsageEntry): void {
    if (!record.renderedOutputId) return;
    const baseUrl = this.configService.config?.situLinkBaseUrl
      ? `https://view.${this.configService.config.situLinkBaseUrl}`
      : (this.configService.config?.situPortalBaseUrl || window.location.origin);
    const url = new URL(baseUrl);
    url.pathname = record.renderedOutputId;
    window.open(url.toString(), '_blank');
  }

  viewProduct(record: ProductUsageEntry): void {
    if (!record.productId) return;
    window.open(`${window.location.origin}/products/${record.productId}`, '_blank');
  }

  getRoomsList(record: ProductUsageEntry): string {
    return (record.rooms || []).map(r => `${r.roomName} (${r.count})`).join(', ') || '—';
  }

  refreshData(): void {
    this.loadProductUsage();
  }

  exportResults(): void {
    if (this.allRecords.length === 0) return;
    const impressionsHeader = `Impressions (${this.impressionWindowHours}h)`;
    const headers = ['Home', 'Company', 'Sightings', 'Replacements', 'Hotspots', impressionsHeader, 'Rooms', 'Rendered Output ID'];
    const rows = this.allRecords.map(r => [
      r.homeName || '', r.companyName || '',
      r.sightingCount ?? 0, r.replacementCount ?? 0, r.hotspotCount ?? 0,
      (r as any).impressionCount ?? 0,
      (r.rooms || []).map(rm => rm.roomName).join('; '),
      r.renderedOutputId || ''
    ]);
    const csv = [headers.join(','), ...rows.map(row => row.map(c => `"${c}"`).join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `product-${this.product?.sku || 'usage'}-${Date.now()}.csv`;
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
}
